import agencyDashboardAPI from '@/utils/api/agencyDashboard';
import commonAPI from '@/utils/api/common';
import { genericError } from '@/utils/alerts.service';
import { fileUpload } from '@/store/vendorDashboard/modules/fileUpload';
import { charCountForHtmlContent } from '@/utils/Inputs/htmlManipulationFunctions';

export const ourPublicProfilesDetail = {
  namespaced: true,
  state: {
    profile: [],
    locations: [],
    countries: [],
    states: [],
    cities: [],
    clients: [],
    resources: [],
    testimonials: [],
    pastWork: [],
    videos: [],
    locationsToDelete: [],
    pastWorkToDelete: [],
    videosToDelete: [],
    clientsToDelete: [],
    resourcesToDelete: [],
    testimonialsToDelete: [],
    filters: [],
    customButton: {},
    errors: {},
    profileDirty: false,
    profileLocationDirty: false,
    additionalLocationsDirty: false,
    locationsDirty: false,
    testimonialsDirty: false,
    videosDirty: false,
    pastWorkDirty: false,
    clientsDirty: false,
    resourcesDirty: false,
    buttonDirty: false,
  },

  getters: {
    profile(state) {
      return state.profile;
    },

    locations(state) {
      return state.locations;
    },

    newLocations(state) {
      return state.newLocations;
    },

    countries(state) {
      return state.countries;
    },

    states(state) {
      return state.states;
    },

    cities(state) {
      return state.cities;
    },

    filters(state) {
      return state.filters;
    },

    featuredContent(state) {
      return state.featuredContent;
    },

    pastWork(state) {
      return state.pastWork.filter(item => !item.delete);
    },

    videos(state) {
      return state.videos.filter(item => !item.delete);
    },

    clients(state) {
      return state.clients.filter(item => !item.delete);
    },

    resources(state) {
      return state.resources.filter(item => !item.delete);
    },

    testimonials(state) {
      return state.testimonials;
    },

    checkboxFilters(state) {
      return state.filters.filter(
        filter => filter.multiple && !filter.onlyMatchmaking
      );
    },

    dropdownFilters(state) {
      return state.filters.filter(
        filter => !filter.multiple && !filter.onlyMatchmaking
      );
    },

    showHeaderBar(state) {
      return (
        state.profileDirty ||
        state.profileLocationDirty ||
        state.additionalLocationsDirty ||
        state.locationsDirty ||
        state.testimonialsDirty ||
        state.videosDirty ||
        state.pastWorkDirty ||
        state.clientsDirty ||
        state.resourcesDirty
      );
    },
  },

  mutations: {
    setProfile(state, payload) {
      state.profile = payload;
    },

    setLocations(state, locations) {
      state.locations = locations;
    },

    setCountries(state, countries) {
      state.countries = countries;
    },

    setError(state, data) {
      state.errors[data.key] = {
        label: data.label,
        state: data.state,
        message: data.message,
      };
    },

    setClients(state, clients) {
      state.clients = clients;
    },

    setFeaturedContent(state, content) {
      state.featuredContent = content;
    },

    setResources(state, resources) {
      state.resources = resources;
    },

    setTestimonials(state, testimonials) {
      state.testimonials = testimonials;
    },

    setPastWork(state, pastWork) {
      state.pastWork = pastWork;
    },

    setVideos(state, videos) {
      state.videos = videos;
    },

    setStates(state, states) {
      state.states = states;
    },

    setCities(state, cities) {
      state.cities = cities;
    },

    setFilters(state, filters) {
      state.filters = filters;
    },

    setImage(state, payload) {
      state.profile[payload.field] = payload.uri;
      state.profileDirty = true;
    },

    updateProfile(state, payload) {
      state.profile[payload.key] = payload.value;
      state.profileDirty = true;
    },
    addFilter(state, id) {
      state.profile.filterOptions.push(id);
      state.profileDirty = true;
    },

    removeFilter(state, id) {
      state.profile.filterOptions = state.profile.filterOptions.filter(
        filter => {
          return filter !== id;
        }
      );
      state.profileDirty = true;
    },

    setProfileLocationDirty(state) {
      state.profileLocationDirty = true;
    },

    addLocation(state) {
      state.newLocations.push({});
    },

    updateLocation(state, payload) {
      state.newLocations[payload.index][payload.key] = payload.value;
    },

    markLocationForDelete(state, index) {
      state.locations[index].delete = true;
    },

    removeNewLocation(state, index) {
      state.newLocations.splice(index, 1);
    },

    setLocationDirty(state) {
      state.locationsDirty = true;
    },

    setButtonDirty(state) {
      state.buttonDirty = true;
    },

    updateCustomButton(state, payload) {
      state.customButton[payload.key] = payload.value;
    },

    updatePastWork(state, payload) {
      state.pastWork = payload;
    },

    updateVideos(state, payload) {
      state.videos = payload;
    },

    updateClients(state, payload) {
      state.clients = payload;
    },

    updateResources(state, payload) {
      state.resources = payload;
    },

    deleteItem(state, payload) {
      // Need to mark item for deletion
      state[payload.section][payload.item.order || 0]['delete'] = true;

      // Need to keep track of deleting items to merge with rendered array on save
      state[payload.section + 'ToDelete'].push(
        state[payload.section][payload.item.order || 0]
      );

      // Remove deleted items from rendered array
      state[payload.section] = state[payload.section].filter(i => {
        return !i.delete;
      });

      // Need to reassign order since one item was removed
      state[payload.section] = state[payload.section].map((item, i) => {
        return {
          ...item,
          order: i,
        };
      });
    },

    addItem(state, payload) {
      state[payload.section].push(payload.data);
    },

    editItem(state, payload) {
      state[payload.section][payload.data.order || 0] = payload.data;

      if (
        (payload.data.order >= 0 || payload.section === 'testimonials') &&
        payload.data.id
      ) {
        state[payload.section][payload.data.order || 0]['update'] = true;
      }

      state[payload.section] = [...state[payload.section]];
    },

    markSectionDirty(state, item) {
      state[item + 'Dirty'] = true;
    },

    clearDirty(state) {
      state.profileDirty = false;
      state.profileLocationDirty = false;
      state.locationsDirty = false;
      state.additionalLocationsDirty = false;
      state.testimonialsDirty = false;
      state.videosDirty = false;
      state.pastWorkDirty = false;
      state.clientsDirty = false;
      state.resourcesDirty = false;
    },

    clearToDelete(state) {
      state.locationsToDelete = [];
      state.pastWorkToDelete = [];
      state.videosToDelete = [];
      state.clientsToDelete = [];
      state.resourcesToDelete = [];
      state.testimonialsToDelete = [];
    },

    clearAllData(state) {
      state.profile = [];
      state.locations = [];
      state.countries = [];
      state.states = [];
      state.cities = [];
      state.clients = [];
      state.resources = [];
      state.testimonials = [];
      state.pastWork = [];
      state.videos = [];
      state.filters = [];
    },
  },

  actions: {
    ...fileUpload.actions,

    clearToDelete({ commit }) {
      commit('clearToDelete');
    },

    clearAllData({ commit }) {
      commit('clearToDelete');
      commit('clearAllData');
    },

    deleteItem({ commit }, payload) {
      commit('deleteItem', payload);
      commit('markSectionDirty', payload.section);
    },

    addItem({ commit }, payload) {
      commit('addItem', payload);
      commit('markSectionDirty', payload.section);
    },

    editItem({ commit }, payload) {
      commit('editItem', payload);
      commit('markSectionDirty', payload.section);
    },

    updateProfile({ commit }, payload) {
      commit('updateProfile', payload);
    },

    setButtonDirty({ commit }) {
      commit('setButtonDirty');
    },

    updateCustomButton({ commit }, payload) {
      commit('updateCustomButton', payload);
    },

    updateError({ commit }, payload) {
      commit('setError', payload);
    },

    updateProfileLocation({ dispatch, commit }, payload) {
      dispatch('updateLocation', payload, { root: true });
      commit('setProfileLocationDirty');
    },

    updateFilters({ commit, state }, id) {
      for (let appliedFilter of state.profile.filterOptions) {
        if (appliedFilter === id) {
          return commit('removeFilter', id);
        }
      }
      return commit('addFilter', id);
    },

    handleCheckboxChange({ commit, state }, filter) {
      let add = true;

      state.profile.filterOptions.forEach(option => {
        if (option === filter) {
          add = false;
        }
      });

      if (add) {
        return commit('addFilter', filter);
      }

      return commit('removeFilter', filter);
    },

    handleDropdownChange({ commit, state }, payload) {
      const partnerFilters = state.profile.filterOptions;
      const dirFilters = state.filters;
      let changedFilter = undefined;

      for (let filter of dirFilters) {
        if (filter.id === payload.filter) {
          changedFilter = filter;
        }
      }

      // eslint-disable-next-line no-unused-vars
      for (const [i, v] of partnerFilters.entries()) {
        for (let option of changedFilter.filterOptions) {
          if (v === option.id) {
            commit('removeFilter', v);
          }
        }
      }

      if (payload.option) {
        commit('addFilter', payload.option);
      }
    },

    addLocation({ commit }) {
      commit('addLocation');
    },

    updateLocation({ commit }, payload) {
      commit('updateLocation', payload);
      commit('setLocationDirty');
    },

    removeLocation({ commit }, index) {
      commit('markLocationForDelete', index);
      commit('setLocationDirty');
    },

    removeNewLocation({ commit }, index) {
      commit('removeNewLocation', index);
    },

    async fetchProfile({ dispatch, commit }, id) {
      return agencyDashboardAPI
        .getPublicProfile(id)
        .then(data => {
          commit('setProfile', data);

          return agencyDashboardAPI
            .getFilters(data.directory)
            .then(data => {
              commit('setFilters', data.results);
              return;
            })
            .catch(() => {
              dispatch('showUserMessage', { ...genericError }, { root: true });
            });
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async fetchLocations({ dispatch, commit }) {
      return agencyDashboardAPI
        .getLocations()
        .then(data => {
          const results = data.results.map(i => {
            // to get data in correct format for front-end to work with
            return {
              id: i.id,
              city: {
                key: i.cityObj.name,
                value: i.city,
              },
              country: {
                key: i.countryObj.name,
                value: i.country,
              },
              region: {
                key: i.regionObj.name,
                value: i.region,
              },
            };
          });
          commit('setLocations', results);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async fetchClients({ dispatch, commit }, id) {
      return agencyDashboardAPI
        .getClients(id)
        .then(data => {
          data.sort((a, b) => {
            if (a.order < b.order) return -1;
            if (a.order > b.order) return 1;
            return 0;
          });
          commit('setClients', data);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async fetchFeaturedContent({ dispatch, commit }, id) {
      return agencyDashboardAPI
        .getFeaturedContent(id)
        .then(data => {
          const pastWork = data.filter(i => !i.url);
          const videos = data.filter(i => i.url);
          pastWork.sort((a, b) => {
            if (a.order < b.order) return -1;
            if (a.order > b.order) return 1;
            return 0;
          });
          videos.sort((a, b) => {
            if (a.order < b.order) return -1;
            if (a.order > b.order) return 1;
            return 0;
          });
          commit('setPastWork', pastWork);
          commit('setVideos', videos);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async fetchResources({ dispatch, commit }, id) {
      return agencyDashboardAPI
        .getResources(id)
        .then(data => {
          data.sort((a, b) => {
            if (a.order < b.order) return -1;
            if (a.order > b.order) return 1;
            return 0;
          });
          commit('setResources', data);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async fetchTestimonials({ dispatch, commit }, id) {
      return agencyDashboardAPI
        .getTestimonials(id)
        .then(data => {
          if (
            !data[0].testimonialText &&
            !data[0].authorName &&
            !data[0].companyName &&
            !data[0].position
          ) {
            return commit('setTestimonials', []);
          }
          commit('setTestimonials', data);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async fetchCountries({ commit, dispatch }) {
      return commonAPI
        .getCountries()
        .then(data => {
          commit('setCountries', data);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async fetchStates({ commit, dispatch }, id) {
      return commonAPI
        .getStates(id)
        .then(data => {
          commit('setStates', data);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async fetchCities({ commit, dispatch }, payload) {
      return commonAPI
        .getCities(payload)
        .then(data => {
          commit('setCities', data);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    clearCities({ commit }) {
      commit('setCities', []);
    },

    checkMainFields({ state, commit, rootState }) {
      return new Promise(resolve => {
        // Error check logo
        if (!state.profile.renderImg || state.profile.renderImg === '') {
          commit('setError', {
            key: 'profileLogo',
            state: true,
            label: 'Profile logo',
          });
          document
            .querySelector('#agencyCompanyLogo .file-input__area')
            .classList.add('fileError');
        } else {
          commit('setError', {
            key: 'profileLogo',
            state: false,
            label: 'Profile logo',
          });
        }
        // Error check name
        if (!state.profile.name || state.profile.name === '') {
          commit('setError', {
            key: 'profileName',
            state: true,
            label: 'Profile name',
          });
          document
            .getElementById('agencyCompanyName')
            .classList.add('inputError');
        } else {
          commit('setError', {
            key: 'profileName',
            state: false,
            label: 'Profile name',
          });
        }
        // Error check about
        if (charCountForHtmlContent(state.profile.renderAboutUs) === 0) {
          commit('setError', {
            key: 'profileAbout',
            state: true,
            label: 'Profile description',
          });
          document
            .getElementById('agencyCompanyAbout')
            .classList.add('contentError');
          document
            .getElementById('agencyCompanyAbout')
            .parentNode.querySelector('.editorButtons')
            .classList.add('buttonsError');
          document
            .getElementById('agencyCompanyAbout')
            .parentNode.querySelector('.countArea')
            .classList.add('countError');
        } else {
          commit('setError', {
            key: 'profileAbout',
            state: false,
            label: 'Profile description',
          });
        }
        // Error check website
        if (
          !state.profile.renderWebsite ||
          state.profile.renderWebsite === '' ||
          !/^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
            String(state.profile.renderWebsite).toLowerCase()
          )
        ) {
          commit('setError', {
            key: 'profileWebsite',
            state: true,
            label: 'Profile website',
            message:
              'Please enter a valid website URL, containing either http:// or https://',
          });
          document
            .getElementById('agencyCompanyWebsite')
            .classList.add('inputError');
        } else {
          commit('setError', {
            key: 'profileWebsite',
            state: false,
            label: 'Profile website',
          });
        }
        // Primary Location Check
        if (
          !rootState[''].agencyProfile.country ||
          rootState[''].agencyProfile.country === ''
        ) {
          commit('setError', {
            key: 'primaryCountry',
            state: true,
            label: 'Primary country',
          });
          document
            .getElementById('agencyCompanyCountry')
            .parentNode.classList.add('inputError');
        } else {
          commit('setError', {
            key: 'primaryCountry',
            state: false,
            label: 'Primary country',
          });
        }
        if (
          !rootState[''].agencyProfile.city ||
          rootState[''].agencyProfile.city === ''
        ) {
          commit('setError', {
            key: 'primaryCity',
            state: true,
            label: 'Primary city',
          });
          document
            .getElementById('agencyCompanyCity')
            .classList.add('inputError');
        } else {
          commit('setError', {
            key: 'primaryCity',
            state: false,
            label: 'Primary city',
          });
        }
        if (
          rootState[''].agencyProfile.country === 'US' &&
          rootState[''].agencyProfile.state === ''
        ) {
          commit('setError', {
            key: 'primaryState',
            state: true,
            label: 'Primary state',
          });
          document
            .getElementById('agencyCompanyState')
            .parentNode.classList.add('inputError');
        } else {
          commit('setError', {
            key: 'primaryState',
            state: false,
            label: 'Primary state',
          });
        }
        resolve();
      });
    },

    saveChanges({ dispatch, state, commit }, id) {
      dispatch('checkMainFields').then(() => {
        if (Object.keys(state.errors).length) {
          for (let key of Object.keys(state.errors)) {
            if (state.errors[key].state) {
              dispatch(
                'showUserMessage',
                {
                  success: false,
                  title: 'Oops!',
                  message:
                    state.errors[key].message ||
                    'Please fill in the ' + state.errors[key].label + ' field.',
                },
                { root: true }
              );
              return;
            }
          }
        }

        let promises = [];

        if (state.profileDirty) {
          promises.push(dispatch('savePublicProfile', id));
        } else {
          commit('addSaveProgress', null, { root: true });
        }
        if (state.profileLocationDirty) {
          promises.push(dispatch('saveProfile', true, { root: true }));
        } else {
          commit('addSaveProgress', null, { root: true });
        }
        if (state.locationsDirty) {
          promises.push(dispatch('saveAdditionalLocations', id));
        } else {
          commit('addSaveProgress', null, { root: true });
        }
        if (state.testimonialsDirty) {
          promises.push(dispatch('saveTestimonials', id));
        } else {
          commit('addSaveProgress', null, { root: true });
        }
        if (state.videosDirty || state.pastWorkDirty) {
          promises.push(dispatch('saveFeaturedContent', id));
        } else {
          commit('addSaveProgress', null, { root: true });
        }
        if (state.clientsDirty) {
          promises.push(dispatch('saveClients', id));
        } else {
          commit('addSaveProgress', null, { root: true });
        }
        if (state.resourcesDirty) {
          promises.push(dispatch('saveResources', id));
        } else {
          commit('addSaveProgress', null, { root: true });
        }

        commit('resetSaveProgress', null, { root: true });
        commit('setSaveTotalItems', promises.length + 1, { root: true });
        commit('setSaving', 'saving', { root: true });
        commit('addSaveProgress', null, { root: true });

        Promise.all(promises).then(() => {
          dispatch(
            'showUserMessage',
            {
              success: true,
              title: 'Success!',
              message: 'Your changes have been made.',
            },
            { root: true }
          );

          commit('clearDirty');
          commit('setSaving', 'idle', { root: true });
        });
      });
    },

    saveFeaturedContent({ state, commit }, id) {
      return new Promise((resolve, reject) => {
        const content = [
          ...state.videos,
          ...state.pastWork,
          ...state.pastWorkToDelete,
          ...state.videosToDelete,
        ];

        const data = content.map(i => {
          if (i.uri) {
            return {
              ...i,
              file: i.uri,
              uri: null,
            };
          }
          return { ...i };
        });

        return agencyDashboardAPI
          .saveFeaturedContent(id, data)
          .then(res => {
            const pastWork = res.data.filter(i => !i.url);
            const videos = res.data.filter(i => i.url);

            pastWork.sort((a, b) => {
              if (a.order < b.order) return -1;
              if (a.order > b.order) return 1;
              return 0;
            });
            videos.sort((a, b) => {
              if (a.order < b.order) return -1;
              if (a.order > b.order) return 1;
              return 0;
            });

            commit('setPastWork', pastWork);
            commit('setVideos', videos);
            commit('addSaveProgress', null, { root: true });
            resolve();
          })
          .catch(() => {
            reject();
          });
      });
    },

    saveClients({ state, commit }, id) {
      return new Promise((resolve, reject) => {
        const data = [...state.clients, ...state.clientsToDelete];
        return agencyDashboardAPI
          .saveClients(id, data)
          .then(res => {
            res.data.sort((a, b) => {
              if (a.order < b.order) return -1;
              if (a.order > b.order) return 1;
              return 0;
            });
            commit('setClients', res.data);
            commit('addSaveProgress', null, { root: true });
            resolve();
          })
          .catch(() => {
            reject();
          });
      });
    },

    saveResources({ state, commit }, id) {
      return new Promise((resolve, reject) => {
        const resources = state.resources.map(r => {
          if (r.resourceUrl && r.file === '') {
            delete r.file;
          }
          if (r.uri) {
            return {
              ...r,
              file: r.uri,
              uri: null,
            };
          }
          return r;
        });
        const data = [...resources, ...state.resourcesToDelete];
        return agencyDashboardAPI
          .saveResources(id, data)
          .then(res => {
            res.data.sort((a, b) => {
              if (a.order < b.order) return -1;
              if (a.order > b.order) return 1;
              return 0;
            });
            commit('setResources', res.data);
            commit('addSaveProgress', null, { root: true });
            resolve();
          })
          .catch(() => {
            reject();
          });
      });
    },

    saveTestimonials({ state, commit }, id) {
      return new Promise((resolve, reject) => {
        const data = [...state.testimonials, ...state.testimonialsToDelete];
        return agencyDashboardAPI
          .saveTestimonials(id, data)
          .then(res => {
            const item = res.data[0];
            if (
              item &&
              !item.authorName &&
              !item.companyName &&
              !item.position &&
              !item.testimonialText
            ) {
              commit('setTestimonials', []);
            } else {
              commit('setTestimonials', res.data);
            }

            commit('addSaveProgress', null, { root: true });
            resolve();
          })
          .catch(() => {
            reject();
          });
      });
    },

    savePublicProfile({ commit, state }, id) {
      return new Promise((resolve, reject) => {
        const data = {
          ...state.profile,
          website: state.profile.renderWebsite,
          image: state.profile.renderImg,
          aboutUs: state.profile.renderAboutUs,
        };
        return agencyDashboardAPI
          .updatePublicProfile(id, data)
          .then(() => {
            commit('addSaveProgress', null, { root: true });
            resolve();
          })
          .catch(() => {
            reject();
          });
      });
    },

    saveAdditionalLocations({ commit, state }) {
      return new Promise((resolve, reject) => {
        const locations = state.locations.map(location => {
          return {
            ...location,
            country: location.country.value,
            region: location.region.value,
            city: location.city.value,
          };
        });

        const locationsToDelete = state.locationsToDelete.map(location => {
          return {
            ...location,
            country: location.country.value,
            region: location.region.value,
            city: location.city.value,
          };
        });

        const data = [...locations, ...locationsToDelete];
        return agencyDashboardAPI
          .saveLocations(data)
          .then(() => {
            commit('addSaveProgress', null, { root: true });
            resolve();
          })
          .catch(() => {
            reject();
          });
      });
    },
  },
};
